import { node } from "~/util/dom";
import { Scene, _, store } from "~/route/globals";
import Items, { IItemData } from "~/route/items";
import Map from "~/route/map/main";
import { ROUTE_CONST } from "~/route/const";
import { itineraryPubtTypeName } from "~/route/utils";
import { setCoordinates } from "~/state/map/state";
import { isoZoneToUTC, standardTimeFormat, dateToTime } from "~/util/format";
import { CONFIG } from "~/config";

interface IPubtItem {
	[key: string]: any;
}

interface IItemDataPubt extends IItemData {
	departureISO: string;
	arrivalISO: string;
	durationMinute: number;
	items: any;
	payment: Array<any>;
}

const TITLE_LEN = 35;

function openItem(id: number) {
	Mapy.getComponent("detail").setState({
		id,
		source: "pubt",
		show: false,
	}).then(() => Mapy.getComponent("history").save(), () => {});
}

/**
 * Vytvoreni jednoho typu - prijezd/odjezd.
 *
 * @param   isArrival Jedna se o prijezd?
 * @param   item   Data
 */
function makeType(isArrival: boolean, item: IPubtItem): HTMLParagraphElement {
	// eslint-disable-next-line new-cap
	const date = isoZoneToUTC(isArrival ? item.arrivalISO : item.departureISO);
	const id = isArrival ? item.oznEndID : item.oznStartID;
	const title = isArrival ? item.endStopName : item.startStopName;
	const el = node("p", { className: isArrival ? "arrival" : "departure" });
	const elLink = node("a", { href: `https://${CONFIG.BASE_URL}/?source=pubt&id=${id}`, className: "arrival-departure", textContent: title });

	if (title > TITLE_LEN) {
		elLink.title = title;
	}

	elLink.addEventListener("click", event => {
		if (!Mapy.Util.linkToNewWindow(event)) {
			event.preventDefault();
			openItem(id);
		}
	});
	el.appendChild(elLink);
	el.appendChild(node("span", { className: "time", title: Mapy.Util.dateToLocaleFormat(date, true, false), textContent: dateToTime(date) }));

	return el;
}

/**
 * Obal pro:
 * - mhd
 */
export default class ItemPubt {
	_parent: Items;

	_data: IItemDataPubt;

	_map: Map;

	_dom: {
		container: HTMLDivElement;
		inner?: HTMLDivElement;
		position?: HTMLParagraphElement;
	};

	_dates: {
		departureDate: Date;
		arrivalDate: Date;
	};

	_siblings: any;

	_geometries: any;

	_selected: {
		markers: Array<any>;
		geometry: any;
	};

	_reportText: string;

	constructor(parent: Items, data: IItemDataPubt, map: Map) {
		this._parent = parent;
		this._data = data;
		this._map = map;
		this._dom = {
			container: node("div", { className: "route-item-pubt" }),
		};
		this._dates = {
			// eslint-disable-next-line new-cap
			departureDate: isoZoneToUTC(this._data.departureISO),
			// eslint-disable-next-line new-cap
			arrivalDate: isoZoneToUTC(this._data.arrivalISO),
		};
		this._siblings = null;
		this._geometries = [];
		this._selected = {
			markers: [],
			geometry: null,
		};
	}

	destroy() {
		this._dom.container.remove();
	}

	get container() {
		return this._dom.container;
	}

	// eslint-disable-next-line class-methods-use-this
	get state() {
		return null;
	}

	set siblings(siblings) {
		this._siblings = siblings;
		this._build();
	}

	/**
	 * Vola se az po nastaveni siblings.
	 */
	_build() {
		const paging = node("div", { className: "paging-pt" });
		const prevBtn = node("button", { className: "prev", textContent: _("route.prevPT") });

		prevBtn.appendChild(document.createTextNode(""));
		const nextBtn = node("button", { className: "next", textContent: _("route.nextPT") });

		nextBtn.appendChild(document.createTextNode(""));
		paging.appendChild(prevBtn);
		paging.appendChild(nextBtn);
		const title = node("h2");

		this._setTitle(title);
		title.appendChild(document.createTextNode(""));
		title.appendChild(node("span", { textContent: "" }));
		const list = node("ul");
		const eshopUrl = this._makeItinerary(list);

		// eventy
		prevBtn.addEventListener("click", () => {
			this._changePosition(-1);
		});
		nextBtn.addEventListener("click", () => {
			this._changePosition(1);
		});

		this._dom.container.appendChild(paging);
		this._dom.container.appendChild(title);
		this._dom.container.appendChild(list);

		if (eshopUrl) {
			const eshopLink = node("a", { className: "eshop", innerHTML: _("route.buyTicket"), target: "_blank", href: eshopUrl });

			this._dom.container.appendChild(eshopLink);
		}
	}

	/**
	 * Nastaveni titulku iterinare.
	 *
	 * @param  titleEl Element pro nastaveni
	 */
	_setTitle(titleEl: HTMLElement) {
		titleEl.innerHTML = `${dateToTime(this._dates.departureDate, false, true)} &ndash; ${dateToTime(this._dates.arrivalDate)}`;
		const span = node("span", { textContent: standardTimeFormat(this._data.durationMinute * 60).string });

		titleEl.appendChild(span);
		titleEl.title = `${Mapy.Util.dateToLocaleFormat(this._dates.departureDate, true, false)} – ${Mapy.Util.dateToLocaleFormat(this._dates.arrivalDate, true, false)}`;
	}

	/**
	 * Vytvoreni itinerare.
	 *
	 * @param  listEl Do jakeho seznamu polozky pripnout?
	 * @return vraci url pro eshop
	 */
	_makeItinerary(listEl: HTMLUListElement): string {
		this._geometries.length = 0;

		const lines = [];
		let eshopURL = "";

		this._data.items.forEach((item, index) => {
			const li = node("li");

			// jizda
			if (item.type === 0) {
				lines.push(item.routeName);
				li.classList.add(itineraryPubtTypeName(item.vehicleType, item.routeName));
				const h3 = node("h3", { className: "line ", title: item.routeName });
				const h3LinkHref = `https://${CONFIG.BASE_URL}/?source=pubt&id=${item.oznStartID}&timeboard=${encodeURIComponent(JSON.stringify({ l: item.routeLongName, d: item.direction }))}`;
				const h3Link = node("a", { href: h3LinkHref, title: item.routeName, textContent: item.routeName });

				h3Link.addEventListener("click", event => {
					if (!Mapy.Util.linkToNewWindow(event)) {
						event.preventDefault();
						Mapy.getComponent("detail").setState({
							source: "pubt",
							id: item.oznStartID,
						}).then(() => {
							Mapy.getComponent("timeboard").setSheet(item.routeLongName, item.direction, undefined, undefined, item.oznStartID);
							Mapy.getComponent("detail").openTimeboard();
							Mapy.getComponent("history").save();
						}, () => {});
					}
				});
				h3.appendChild(h3Link);
				li.appendChild(h3);

				const departure = makeType(false, item);

				li.appendChild(departure);

				const arrival = makeType(true, item);

				li.appendChild(arrival);
			}

			// pesi presun + cekani
			if (item.type === 1) {
				// eslint-disable-next-line new-cap
				let departureDateItem = isoZoneToUTC(item.departureISO);
				// eslint-disable-next-line new-cap
				let arrivalDateItem = isoZoneToUTC(item.arrivalISO);
				let durationMinute = Math.abs(arrivalDateItem.getTime() - departureDateItem.getTime()) / 60000;

				if (durationMinute > 0.8) {
					durationMinute = durationMinute < 1 ? 1 : Math.floor(durationMinute);
					li.classList.add("pedestrian");
					const first = index === 0;

					// prvni pesky je odnekud na zastavku; posledni pesky je pro zmenu ze zastavky nekam
					if (first || index === this._data.items.length - 1) {
						const paragraph = node("p", { textContent: _(`route.result.pt.${first ? "toStation" : "toDestination"}`) });
						const spanTime = node("span", { className: "time", innerHTML: `${durationMinute}&thinsp;min` });

						paragraph.appendChild(spanTime);
						li.appendChild(paragraph);
					} else { // cokoliv jineho
						if ((index + 1) < this._data.items.length && this._data.items[index + 1].type === 2) {
							// eslint-disable-next-line new-cap
							departureDateItem = isoZoneToUTC(this._data.items[index + 1].departureISO);
							// eslint-disable-next-line new-cap
							arrivalDateItem = isoZoneToUTC(this._data.items[index + 1].arrivalISO);
							const diff = Mapy.Util.timeDiff(departureDateItem, arrivalDateItem);

							durationMinute += diff.minutesTotal;
						}

						const paragraph = node("p", { textContent: _(`route.result.pt.transfer`) });
						const spanTime = node("span", { className: "time", innerHTML: `${Math.round(durationMinute)}&thinsp;min` });

						paragraph.appendChild(spanTime);
						li.appendChild(paragraph);
					}

					li.addEventListener("click", () => {
						const geometries = this._geometries.filter(geometry => geometry.item === item);

						if (geometries.length) {
							const geom = geometries[0];
							const cz = Scene.computeCenterZoom(geom.coords);
							const [longitude, latitude] = cz[0].toWGS84();

							store.dispatch(setCoordinates({
								longitude,
								latitude,
								zoom: cz[1],
								animate: true,
							}));
						}
					});
				} else {
					return;
				}
			}

			if ("payment" in item) {
				for (let paymentIndex = 0; paymentIndex < item.payment.length; paymentIndex++) {
					if (item.payment[paymentIndex].type === "price" && item.payment[paymentIndex].url) {
						eshopURL = item.payment[paymentIndex].url;
					}
				}
			}

			// cekani
			if (item.type === 2) {
				return;
			}

			// vytvoreni markeru a geometrii
			if (item.geometry) {
				const options = {
					...ROUTE_CONST.DEFAULT_LINE_STYLE,
					...ROUTE_CONST.PUBT_LINE_STYLE_MODIFY,
					...ROUTE_CONST.PUBT_HOVER_LINE_STYLE_MODIFY,
				};
				const coords = Scene.stringToCoords(item.geometry);

				delete options.exactStyle;
				delete options.style;

				if (this._geometries.length) {
					const prevItem = this._geometries[this._geometries.length - 1];

					if (prevItem.item.type === 1) {
						const coor = prevItem.geometry.getCoords();

						coor.push(coords[0]);
						prevItem.geometry.setCoords(coor);
					} else {
						const prevCoords = prevItem.geometry.getCoords();

						coords.unshift(prevCoords[prevCoords.length - 1]);
					}
				}

				const geometry = Scene.createGeometry(Scene.GEOMETRY.POLYLINE, null, coords, options);

				this._geometries.push({
					item,
					geometry,
					markers: [],
					defaultOpacity: options.opacity,
					defaultOutlineOpacity: options.outlineOpacity,
					coords,
				});
			}

			li.addEventListener("mouseenter", () => {
				this._enter(item);
			});
			li.addEventListener("mouseleave", () => {
				this._leave();
			});
			listEl.appendChild(li);
		});

		if ("payment" in this._data) {
			for (let paymentIndex = 0; paymentIndex < this._data.payment.length; paymentIndex++) {
				if (this._data.payment[paymentIndex].type === "eshop") {
					eshopURL = this._data.payment[paymentIndex].url;
				}
			}
		}

		this._reportText = `${_("route.result.pt.mhdInTime")} ${dateToTime(this._dates.departureDate, false, true)} (${Mapy.Util.dateToLocaleFormat(this._dates.departureDate)})`;
		this._reportText += ` – ${dateToTime(this._dates.arrivalDate, false, true)} (${Mapy.Util.dateToLocaleFormat(this._dates.arrivalDate)})\n${_("route.result.pt.mhdLinks")}: ${lines.join(", ")}`;

		// poresime markery
		this._geometries.forEach((geom, ind) => {
			let skipPrev = false;
			let skipNext = false;
			const coords = [];

			if (ind === 0 && this._siblings.prevIsFirst) {
				skipPrev = true;
			}
			if (ind === this._geometries.length - 1 && this._siblings.nextIsLast) {
				skipNext = true;
			}

			if (!skipPrev) {
				coords.push(geom.coords[0]);
			}
			if (!skipNext) {
				coords.push(geom.coords[geom.coords.length - 1]);
			}
			coords.map(coord => {
				geom.markers.push(Scene.createMarkerWindy(coord));
			});
		});

		return eshopURL;
	}

	/**
	 * Zmena pozice linky.
	 *
	 * @param   direction -1/1
	 */
	_changePosition(direction: number) {
		this._parent.updatePubtDirection(this, direction);
		Mapy.CML.state = {
			route: {
				forced: true,
			},
		};
	}

	/**
	 * Najeti na polozku u itinerare.
	 *
	 * @param   item  Polozka z geometries
	 */
	_enter(item) {
		const geometries = this._geometries.filter(geometry => geometry.item === item);

		if (geometries.length) {
			const geom = geometries[0];

			this._map.activeGeomLayer.addGeometry(geom.geometry);
			this._selected.geometry = geom.geometry;
			this._map.markerLayer.addMarker(geom.markers);
			this._selected.markers = geom.markers;
		}
	}

	/**
	 * Odjeti z polozky u itinerare.
	 */
	_leave() {
		if (this._selected.geometry && this._selected.geometry.appended) {
			this._map.activeGeomLayer.removeGeometry(this._selected.geometry);
		}
		this._selected.geometry = null;

		if (this._selected.markers) {
			this._map.markerLayer.removeMarker(this._selected.markers);
			this._selected.markers = null;
		}
	}
}
